import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  clearAllStationFaults,
  createSpeedLimitZone,
  createStation,
  getDocksByCity,
  getSpeedLimitZoneById,
  getSpeedLimitZones,
  getStationById,
  getStationStatuses,
  getStations,
  getStationsByCity,
  getStationVehicleStatuses,
  refreshStation,
  reloginStation,
  type SpeedLimitZoneCreate,
  type SpeedLimitZoneUpdate,
  type StationCreate,
  type StationListParams,
  type StationUpdate,
  updateSpeedLimitZone,
  updateStation,
} from "@/apis/station/station-apis";
import { queryKeys } from "./keys";

export function useStations(params: StationListParams = {}, enabled = true) {
  return useQuery({
    queryKey: queryKeys.stations.list(params),
    queryFn: () => getStations(params),
    enabled,
    staleTime: 5 * 60 * 1000,
    placeholderData: keepPreviousData,
  });
}

export function useStation(id: number | null) {
  return useQuery({
    queryKey: queryKeys.stations.detail(id ?? 0),
    // Query is disabled when id is null, so queryFn won't run with fallback value
    queryFn: () => getStationById(id ?? 0),
    enabled: id !== null,
  });
}

export function useStationStatuses() {
  return useQuery({
    queryKey: queryKeys.enums.stationStatuses,
    queryFn: getStationStatuses,
    staleTime: Infinity,
  });
}

export function useStationVehicleStatuses() {
  return useQuery({
    queryKey: queryKeys.enums.stationVehicleStatuses,
    queryFn: getStationVehicleStatuses,
    staleTime: Infinity,
  });
}

export function useCreateStation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (dto: StationCreate) => createStation(dto),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.stations.all,
        predicate: (query) => query.queryKey[1] === "list",
      });
    },
  });
}

export function useUpdateStation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, dto }: { id: number; dto: StationUpdate }) =>
      updateStation(id, dto),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.stations.detail(id),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.stations.all,
        predicate: (query) => query.queryKey[1] === "list",
      });
    },
  });
}

export function useRefreshStation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => refreshStation(id),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.stations.detail(id),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.stations.all,
        predicate: (query) => query.queryKey[1] === "list",
      });
    },
  });
}

export function useReloginStation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => reloginStation(id),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.stations.detail(id),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.stations.all,
        predicate: (query) => query.queryKey[1] === "list",
      });
    },
  });
}

export function useClearAllStationFaults() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => clearAllStationFaults(id),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.stations.detail(id),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.stations.all,
        predicate: (query) => query.queryKey[1] === "list",
      });
    },
  });
}

export function useSpeedLimitZones(params: StationListParams) {
  return useQuery({
    queryKey: queryKeys.stations.speedLimitZones(params),
    queryFn: () => getSpeedLimitZones(params),
    placeholderData: keepPreviousData,
  });
}

export function useSpeedLimitZone(id: number | null) {
  return useQuery({
    queryKey: queryKeys.stations.speedLimitZoneDetail(id ?? 0),
    // Query is disabled when id is null, so queryFn won't run with fallback value
    queryFn: () => getSpeedLimitZoneById(id ?? 0),
    enabled: id !== null,
  });
}

export function useCreateSpeedLimitZone() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (dto: SpeedLimitZoneCreate) => createSpeedLimitZone(dto),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.stations.all,
        predicate: (query) => query.queryKey[1] === "speedLimitZones",
      });
    },
  });
}

export function useUpdateSpeedLimitZone() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, dto }: { id: number; dto: SpeedLimitZoneUpdate }) =>
      updateSpeedLimitZone(id, dto),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.stations.speedLimitZoneDetail(id),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.stations.all,
        predicate: (query) => query.queryKey[1] === "speedLimitZones",
      });
    },
  });
}

export function useStationsByCity(cityId: number | null) {
  return useQuery({
    queryKey: [...queryKeys.stations.all, "byCity", cityId] as const,
    queryFn: () => getStationsByCity(cityId),
    enabled: cityId !== null,
    staleTime: 5 * 60 * 1000,
  });
}

export function useDocksByCity(cityId: number | null) {
  return useQuery({
    queryKey: [...queryKeys.docks.all, "byCity", cityId] as const,
    queryFn: () => getDocksByCity(cityId),
    enabled: cityId !== null,
    staleTime: 5 * 60 * 1000,
  });
}
